import { useEffect, useState } from 'react';
import { Link, Navigate, useParams } from 'react-router-dom';
import Seo from '@/components/feature/Seo';
import { chapterLists, chapterPages, mangas } from '@/mocks/manga';
import { SITE_URL } from '@/utils/site';

export default function ReadPage() {
  const { mangaId, chapterId } = useParams();
  const [menuOpen, setMenuOpen] = useState(false);

  const manga = mangas.find((m) => m.id === mangaId);
  const chapters = chapterLists.find((c) => c.mangaId === mangaId)?.chapters ?? [];
  const chapter = chapters.find((ch) => ch.id === chapterId);

  useEffect(() => {
    window.scrollTo(0, 0);
    setMenuOpen(false);
  }, [chapterId]);

  if (!manga || !chapter) {
    return <Navigate to="/category" replace />;
  }

  const pages = chapterPages[chapter.id] ?? [];
  const idx = chapters.findIndex((ch) => ch.id === chapter.id);
  const prev = idx > 0 ? chapters[idx - 1] : null;
  const next = idx < chapters.length - 1 ? chapters[idx + 1] : null;

  return (
    <div className="bg-background-200/70 pb-10 pt-24 md:pt-28">
      <Seo
        title={`${manga.title}漫画-${chapter.title}免费阅读_073漫画网`}
        description={`${manga.title}漫画${chapter.title}免费下拉式阅读，滑动屏幕即可畅读最新章节，073漫画网漫画免费阅读、每日更新。`}
        keywords={`${manga.title}漫画,${chapter.title},漫画免费阅读,下拉式漫画,073漫画网`}
        canonicalPath={`/read/${manga.id}/${chapter.id}`}
        type="article"
        image={manga.cover}
        jsonLd={[
          {
            '@context': 'https://schema.org',
            '@type': 'ComicIssue',
            name: `${manga.title}-${chapter.title}`,
            url: `${SITE_URL}/read/${manga.id}/${chapter.id}`,
            image: pages[0] ?? manga.cover,
            description: `${manga.title}漫画${chapter.title}免费下拉式阅读。`,
            isPartOf: {
              '@type': 'ComicSeries',
              name: `${manga.title}漫画`,
              url: `${SITE_URL}/manga/${manga.id}`,
            },
            inLanguage: 'zh-CN',
            datePublished: chapter.updateTime,
          },
        ]}
      />

      <div className="mx-auto max-w-3xl px-4 md:px-6">
        <div className="rounded-lg bg-background-50 p-4 shadow-soft md:p-5">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <Link
              to={`/manga/${manga.id}`}
              className="flex items-center gap-1.5 whitespace-nowrap text-sm text-foreground-700 transition-colors hover:text-primary-600"
            >
              <i className="ri-arrow-left-line" aria-hidden="true" />
              返回目录
            </Link>

            <div className="relative">
              <button
                type="button"
                onClick={() => setMenuOpen((v) => !v)}
                aria-expanded={menuOpen}
                className="flex items-center gap-2 whitespace-nowrap rounded-full bg-background-100 px-4 py-2 text-sm text-foreground-950 transition-colors hover:bg-background-200"
              >
                <i className="ri-list-unordered" aria-hidden="true" />
                {chapter.title}
                <i className="ri-arrow-down-s-line text-foreground-500" aria-hidden="true" />
              </button>

              {menuOpen && (
                <div className="absolute right-0 top-full z-20 mt-2 max-h-72 w-56 overflow-y-auto rounded-lg border border-background-200 bg-background-50 p-1.5 shadow-soft">
                  {chapters.map((ch) => (
                    <Link
                      key={ch.id}
                      to={`/read/${manga.id}/${ch.id}`}
                      className={`block truncate rounded-md px-3 py-2 text-sm transition-colors ${
                        ch.id === chapter.id
                          ? 'bg-primary-100 font-medium text-primary-700'
                          : 'text-foreground-700 hover:bg-background-100'
                      }`}
                    >
                      {ch.title}
                    </Link>
                  ))}
                </div>
              )}
            </div>

            <span className="text-xs text-foreground-600">
              {idx + 1} / {chapters.length}
            </span>
          </div>

          <h1 className="mt-4 font-heading text-lg text-foreground-950 md:text-xl">
            《{manga.title}》漫画 · {chapter.title}
          </h1>
          <p className="mt-1 text-xs text-foreground-600">更新时间：{chapter.updateTime} · 下拉式阅读</p>
        </div>

        {pages.length > 0 ? (
          <div className="mt-4 space-y-4">
            {pages.map((src, i) => (
              <img
                key={i}
                src={src}
                alt={`${manga.title}漫画${chapter.title}第${i + 1}页`}
                title={`${manga.title}漫画免费阅读`}
                className="w-full rounded-lg bg-background-50"
              />
            ))}
          </div>
        ) : (
          <div className="mt-4 flex flex-col items-center gap-3 rounded-lg bg-background-50 px-4 py-16 text-center">
            <span className="flex h-16 w-16 items-center justify-center rounded-full bg-background-100 text-3xl text-foreground-400">
              <i className="ri-image-line" aria-hidden="true" />
            </span>
            <p className="text-sm text-foreground-700">本章图片更新中，请稍后再来或先阅读其他章节～</p>
            {next && (
              <Link
                to={`/read/${manga.id}/${next.id}`}
                className="mt-2 rounded-full bg-primary-500 px-5 py-2 text-sm text-background-50 transition-colors hover:bg-primary-600"
              >
                阅读下一章
              </Link>
            )}
          </div>
        )}

        <div className="mt-6 flex flex-col items-center justify-between gap-3 rounded-lg bg-background-50 p-4 shadow-soft sm:flex-row">
          {prev ? (
            <Link
              to={`/read/${manga.id}/${prev.id}`}
              className="flex w-full items-center justify-center gap-1.5 whitespace-nowrap rounded-full bg-background-100 px-5 py-2.5 text-sm text-foreground-800 transition-colors hover:bg-background-200 sm:w-auto"
            >
              <i className="ri-arrow-left-line" aria-hidden="true" />
              上一章：{prev.title}
            </Link>
          ) : (
            <span className="w-full whitespace-nowrap rounded-full bg-background-100 px-5 py-2.5 text-center text-sm text-foreground-400 sm:w-auto">
              已是第一章
            </span>
          )}

          <Link
            to={`/manga/${manga.id}`}
            className="flex w-full items-center justify-center gap-1.5 whitespace-nowrap rounded-full bg-secondary-100 px-5 py-2.5 text-sm text-secondary-900 transition-colors hover:bg-secondary-200 sm:w-auto"
          >
            <i className="ri-list-unordered" aria-hidden="true" />
            查看全部章节
          </Link>

          {next ? (
            <Link
              to={`/read/${manga.id}/${next.id}`}
              className="flex w-full items-center justify-center gap-1.5 whitespace-nowrap rounded-full bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600 sm:w-auto"
            >
              下一章：{next.title}
              <i className="ri-arrow-right-line" aria-hidden="true" />
            </Link>
          ) : (
            <span className="w-full whitespace-nowrap rounded-full bg-primary-100 px-5 py-2.5 text-center text-sm font-medium text-primary-700 sm:w-auto">
              已是最新章节
            </span>
          )}
        </div>

        <p className="mt-6 text-center text-xs leading-relaxed text-foreground-600">
          《{manga.title}》漫画由073漫画网免费提供，支持电脑端与手机端下拉式阅读。滑动屏幕即可感受力量与汗水的故事，喜欢就收藏作品，追更不迷路！
        </p>
      </div>
    </div>
  );
}